/* eslint-disable no-local-storage/no-local-storage */
import { useWindowEvent } from '@mantine/hooks';
import type {
	AIPersonaOut,
	EmbeddedPromptOut,
	EmbeddedPromptParentOut,
	SecodaAIReferencedObjectOut,
} from '@repo/api-codegen';
import {
	fetchGetPersonas,
	fetchRetrievePrompt,
	getPersonasQueryKey,
	listPromptsQueryKey,
	retrievePromptQueryKey,
	useCreatePrompt,
	useGetAiAccessStatus,
} from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { EmptyState } from '@repo/common/components';
import { EntityType } from '@repo/common/enums/entityType';
import { UserRole } from '@repo/common/enums/UserRole';
import { useLocalStorageBooleanState } from '@repo/common/hooks/useLocalStorageBooleanState';
import type { UseSuspenseQueryResult } from '@tanstack/react-query';
import { useQueryClient, useSuspenseQueries } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';
import { memo, Suspense, useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router';
import { Virtuoso } from 'react-virtuoso';
import { useAuthUser } from '../../api';
import { aiEmbeddedQueryKeyFactory } from '../../api/hooks/ai/constants';
import { useActiveAiPersona } from '../../api/hooks/ai/useActiveAiPersona';
import { useAIFeedback } from '../../api/hooks/ai/useAIFeedback';
import { useTemplateList } from '../../api/hooks/templates';
import { sanitizePromptMessage } from '../../components/AIAssistant/utils';
import { entityModalStore } from '../../components/EntityModal/store';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { useUserOnboarding } from '../../hooks/useUserOnboarding';
import { CreateAgentModal } from '../../pages/AIAgentsPage/CreateAgentModal';
import { useCheckIAMPermission } from '../../utils/authorization/roles';
import { AiChatBody } from './components/AiChatBody';
import { AIChatEmpty } from './components/AIChatEmpty';
import { AiChatSkeleton } from './components/AiChatSkeleton';
import { AIPromptInput } from './components/AIPromptInput';
import { AiViewportWrapper } from './components/AiViewportWrapper';
import { ChatBanner } from './components/ChatBanner';
import { MessageGroup } from './components/MessageGroup';
import { PromptSuggestions } from './components/PromptSuggestions';
import { ScrollDownButton } from './components/ScrollDownButton';
import { SharedChatBanner } from './components/SharedChatBanner';
import { usePrefetchPromptSuggestions } from './hooks/usePrefetchPromptSuggestions';
import { AiChatStore } from './store';
import type { PrefilledAgentData } from './types';

type AiChatControllerProps = {
	initialId?: string;
	isSidebar?: boolean;
	onReferencedObjectClick?: (
		referencedObject: SecodaAIReferencedObjectOut
	) => void;
	startingPointEntityIdentifier?: string;
	shouldShowInlineReferencedObject?: (
		referencedObject: SecodaAIReferencedObjectOut
	) => boolean;
};

const INCREASE_VIEWPORT_BY_TOP = 200;
const INCREASE_VIEWPORT_BY_BOTTOM = 1000;

/**
 * Determines if we should enable increaseViewportBy for Virtuoso.
 *
 * When increaseViewportBy is active and the last prompt enters the viewport while
 * scrolling up, Virtuoso automatically snaps to that prompt. This causes unwanted
 * scroll behavior when slowly scrolling up past the last prompt.
 *
 * This function enables increaseViewportBy only after the user has scrolled more
 * than 100px away from the last prompt in either direction. When scrolling back
 * within the buffer zone, it disables increaseViewportBy to prevent snapping.
 */
function shouldEnableIncreaseViewportBy(
	scrollElement: HTMLElement,
	lastItemIndex: number
): boolean {
	try {
		const lastItemElement = scrollElement.querySelector(
			`[data-index="${lastItemIndex}"]`
		) as HTMLElement | null;
		if (!lastItemElement) return false;

		const itemRect = lastItemElement.getBoundingClientRect();
		const containerRect = scrollElement.getBoundingClientRect();
		// Check if item is more than 100px above the viewport
		const distanceAboveViewport = containerRect.top - itemRect.bottom;
		// Check if item is more than 100px below the viewport
		const distanceBelowViewport = itemRect.top - containerRect.bottom;

		return distanceAboveViewport > 100 || distanceBelowViewport > 100;
	} catch {
		return false;
	}
}

const InternalAiChatController = observer(
	({
		initialId,
		isSidebar = false,
		onReferencedObjectClick,
		startingPointEntityIdentifier,
		shouldShowInlineReferencedObject,
	}: AiChatControllerProps) => {
		const { activeAiPersonaId } = useActiveAiPersona();
		const { user, workspace } = useAuthUser();
		const { hasPermission: canUseAiChartTool } = useCheckIAMPermission({
			v1AllowedRoles: [UserRole.ADMIN, UserRole.EDITOR],
			v2Permission: 'Dashboards.Manage',
		});
		const { dismissViewerOnboardingStepQuestion } = useUserOnboarding();
		const navigate = useNavigate();
		const [hasScrolledPastLastPrompt, setHasScrolledPastLastPrompt] =
			useState(false);

		const { data } = useGetAiAccessStatus({});

		const [
			questionEducationBannerDismissed,
			{ setTrue: dismissQuestionEducationBanner },
		] = useLocalStorageBooleanState('questionEducationBannerShown', false);
		const [
			messageLimitBannerDismissed,
			{ setTrue: dismissMessageLimitBanner },
		] = useLocalStorageBooleanState(
			`messageLimitBannerShown-${initialId}`,
			false
		);

		const queries = useSuspenseQueries({
			queries: [
				{
					queryKey: getPersonasQueryKey({}),
					queryFn: () => fetchGetPersonas({}),
				},
				...(initialId
					? [
							{
								queryKey: retrievePromptQueryKey({
									pathParams: { promptId: initialId },
								}),
								queryFn: () =>
									fetchRetrievePrompt({ pathParams: { promptId: initialId } }),
							},
						]
					: []),
			],
		}) as UseSuspenseQueryResult[];

		const personas = useMemo(
			() => queries?.[0]?.data as AIPersonaOut[],
			[queries]
		);

		const queryClient = useQueryClient();
		const { mutateAsync: createPrompt } = useCreatePrompt({
			onSuccess: () => {
				// Invalidate both codegen listPrompts and our base factory lists
				queryClient.invalidateQueries({
					queryKey: listPromptsQueryKey({}),
				});

				queryClient.invalidateQueries({
					queryKey: aiEmbeddedQueryKeyFactory.allLists(),
				});
			},
		});

		const { data: questionTemplates } = useTemplateList({
			filters: {
				is_default: true,
				entity_type_template: EntityType.question,
			},
		});
		const { showNegativeFeedbackModal, showPositiveFeedbackModal } =
			useAIFeedback();

		const [agentModalOpened, setAgentModalOpened] = useState(false);

		const [prefilledAgentData, setPrefilledAgentData] =
			useState<PrefilledAgentData>({
				title: '',
				prompt: '',
				cronExpression: '0 9 * * *',
			});

		const [store] = useState(
			() =>
				new AiChatStore({
					queryClient,
					initialPrompt: queries?.[1]?.data as EmbeddedPromptParentOut | null,
					createPrompt,
					currentUser: user,
					currentWorkspace: workspace,
					isSidebar,
					startingPointEntityIdentifier,
					initialId,
					onSubmit: (newPromptId: string) => {
						if (isSidebar) {
							return;
						}

						dismissViewerOnboardingStepQuestion();
						if (!initialId || initialId === 'new') {
							navigate(`/ai/${newPromptId}`);
						}
					},
					onCreateQuestion: (
						message: EmbeddedPromptOut,
						parentPrompt: EmbeddedPromptParentOut,
						source: 'prompt' | 'response'
					) => {
						if (!message.prompt) {
							return;
						}

						trackEvent(
							'question/create_from_ai_clicked',
							{ source },
							user,
							workspace
						);

						const sanitizedPrompt = sanitizePromptMessage(message.prompt)
							.replace(/(<([^>]+)>)/gi, '')
							.replace(/\n+/g, ' ');
						const markdownLinkRegex = /!?\[.*?\]\(.*?\)/g;
						const title = sanitizedPrompt.replace(markdownLinkRegex, '').trim();
						const attachments = sanitizedPrompt.match(markdownLinkRegex) || [];

						entityModalStore.open({
							type: EntityType.question,
							template: questionTemplates?.results?.[0],
							title: title || '',
							description: attachments.join('\n') || '',
							metadata: {
								owners: [message.user?.id].filter(Boolean),
								ai_prompt: parentPrompt,
							},
						});
					},
					onShowNegativeFeedbackModal: showNegativeFeedbackModal,
					onShowPositiveFeedbackModal: showPositiveFeedbackModal,
				})
		);
		const { data: promptCompletions, isLoading: isLoadingSuggestions } =
			usePrefetchPromptSuggestions(store);

		// Get context for rendering logic
		const { contextType } = store.promptContext;

		// Show suggestions only for bi_metric and bi_dashboard contexts
		const shouldShowSuggestions = useMemo(
			() => contextType === 'bi_metric' || contextType === 'bi_dashboard',
			[contextType]
		);
		const showInput = useMemo(
			() =>
				!data?.restriction_info.is_restricted &&
				(store.isEmpty || store.currentUserIsOwner),
			[
				data?.restriction_info.is_restricted,
				store.isEmpty,
				store.currentUserIsOwner,
			]
		);

		const handleCreateAgent = useCallback((agentData: PrefilledAgentData) => {
			setPrefilledAgentData(agentData);
			setAgentModalOpened(true);
		}, []);

		const handleCloseAgentModal = useCallback(() => {
			setAgentModalOpened(false);
			setPrefilledAgentData({
				title: '',
				prompt: '',
				cronExpression: '',
			});
		}, []);

		const renderVirtuosoItem = useCallback(
			(index: number, messageId: string) => (
				<MessageGroup
					key={messageId}
					id={messageId}
					store={store}
					onReferencedObjectClick={onReferencedObjectClick}
					shouldShowInlineReferencedObject={shouldShowInlineReferencedObject}
					onCreateAgent={handleCreateAgent}
				/>
			),
			[
				store,
				onReferencedObjectClick,
				shouldShowInlineReferencedObject,
				handleCreateAgent,
			]
		);

		const showQuestionBanner =
			store.showQuestionBanner && !questionEducationBannerDismissed;

		const showMessageLimitBanner =
			store.showMessageLimitBanner && !messageLimitBannerDismissed;

		const showCreditRestrictionBanner = useMemo(
			() => data?.restriction_info.restriction_type === 'insufficient_credits',
			[data?.restriction_info.restriction_type]
		);

		useWindowEvent('dragover', (e) => {
			e.preventDefault();
		});
		useWindowEvent('drop', (e) => {
			e.preventDefault();
		});

		return (
			<>
				<Helmet>
					<title>{store.initialPrompt?.title || 'Secoda AI'}</title>
				</Helmet>
				<AiChatBody
					data-has-submitted-prompt={
						store.hasSubmittedPrompt ? true : undefined
					}
					className="ai-chat-wrapper"
					store={store}
				>
					{!store.currentUserIsOwner &&
						store.currentOwner &&
						!store.isEmpty && <SharedChatBanner user={store.currentOwner} />}
					{store.isEmpty ? (
						<AIChatEmpty
							store={store}
							activePersonaId={activeAiPersonaId}
							personas={personas}
						/>
					) : (
						<Virtuoso
							className="ai-chat-scroll-container"
							ref={store.setScrollerHandle}
							totalCount={store.messageIds.length}
							data={store.messageIds}
							itemContent={renderVirtuosoItem}
							atBottomStateChange={store.setScrollAtBottom}
							onScroll={(e) => {
								const lastItemIndex = store.messageIds.length - 1;
								if (lastItemIndex >= 0) {
									setHasScrolledPastLastPrompt(
										shouldEnableIncreaseViewportBy(
											e.currentTarget,
											lastItemIndex
										)
									);
								}
							}}
							// decreasing top helps with overflow anchor while resizing
							{...(hasScrolledPastLastPrompt && {
								increaseViewportBy: {
									top: INCREASE_VIEWPORT_BY_TOP,
									bottom: INCREASE_VIEWPORT_BY_BOTTOM,
								},
							})}
						/>
					)}
					<AiViewportWrapper pos="relative">
						{showInput && !store.isEmpty && (
							<ScrollDownButton
								shouldShow={!store.scrollAtBottom}
								onClick={() => store.scrollToBottom('end')}
							/>
						)}
						<ChatBanner
							showQuestionEducationBanner={showQuestionBanner}
							showMessageLimitBanner={showMessageLimitBanner}
							showCreditRestrictionBanner={showCreditRestrictionBanner}
							creditRestrictionReason={
								data?.restriction_info.reason || undefined
							}
							onDismissQuestionEducationBanner={dismissQuestionEducationBanner}
							onDismissMessageLimitBanner={dismissMessageLimitBanner}
						/>
						{showInput && shouldShowSuggestions && (
							<PromptSuggestions
								store={store}
								onSelect={(prompt) =>
									store.setInputPromptAndSubmit(prompt, {
										personaId: activeAiPersonaId,
									})
								}
								suggestions={
									store.isLoading ? [] : promptCompletions?.suggestions || []
								}
								isLoading={isLoadingSuggestions}
							/>
						)}
						{showInput && (
							<AIPromptInput
								store={store}
								activePersonaId={activeAiPersonaId}
								personas={personas}
								canUseAiChartTool={canUseAiChartTool}
							/>
						)}
					</AiViewportWrapper>
				</AiChatBody>
				{agentModalOpened && (
					<CreateAgentModal
						onClose={handleCloseAgentModal}
						prefilledData={prefilledAgentData || undefined}
					/>
				)}
			</>
		);
	}
);

function AiChatErrorFallback() {
	return (
		<EmptyState
			title="Unable to load chat"
			description="The chat you're looking for could not be found or you don't have permission to view it."
			iconName="exclamationCircle"
			size="lg"
			includeGoBack={false}
		/>
	);
}

export const AiChatController = memo((props: AiChatControllerProps) => (
	<Suspense fallback={<AiChatSkeleton />}>
		<ErrorBoundary fallback={AiChatErrorFallback}>
			<InternalAiChatController {...props} />
		</ErrorBoundary>
	</Suspense>
));
